<template>
    <DModal
        v-model="upgradeForm.activity_log_comment"
        :title="__('customers.contracts.upgrade_contract_title')"
        :show="showModal"
        :is-big="true"
        is-awaiting-validation
        :show-change-reason-input="true"
        close-color-secondary
        :show-incident-list="true"
        :customer-id="contract?.customer_id ?? 0"
        @setIncident="setIncident"
        @close="$emit('close-modal', false)"
        @clearFields="resetUpgradeForm()"
    >
        <div style="padding: 15px;">
            <!-- Product -->
            <ProductChangesSelection
                v-if="activeProductId !== 0"
                label-col="3"
                select-col="8"
                :label="__('customers.contracts.product')"
                :product-id="activeProductId"
                :ignore-current="false"
                :multiple="false"
                :errors="errors"
                :required="true"
                :available-products="'upgrade'"
                :market-region-id="marketRegionId"
                include-current-product
                @update="onProductSelection"
            />

            <div v-if="activeProductId !== upgradeForm.product_id">
                <!-- Price Plan -->
                <DFormRow
                    :label="__('customers.contracts.product_plan')"
                    :error="errors.plan_id"
                    is-required
                >
                    <DSelect
                        id="product_plan"
                        v-model="upgradeForm.plan_id"
                        :options="pricePlans"
                        :placeholder="__('customers.contracts.select_product_plan')"
                        @update="resetForce"
                    />
                </DFormRow>
            </div>
            <div v-else>
                <Price
                    v-model="upgradeForm.price"
                    :errors="errors"
                    id-suffix="upgrade"
                />

                <ThirdPartyCost
                    v-model="upgradeForm.third_party_cost"
                    :errors="errors"
                    id-suffix="upgrade"
                />

                <Discount
                    v-model:discountName="upgradeForm.discount_name"
                    v-model:discountValue="upgradeForm.discount_value"
                    v-model:discountType="upgradeForm.discount_type"
                    :errors="errors"
                    :discount-types="discountTypes"
                    id-suffix="upgrade"
                />

                <PhaseText
                    v-model="upgradeForm.phase_text"
                    class="mt-4"
                    :errors="errors"
                    id-suffix="upgrade"
                />
            </div>

            <!-- Invoice Changes -->
            <DFormRow
                class="mt-4"
                :label="__('customers.contracts.invoice_changes')"
                :error="errors.invoice_changes"
                is-required
            >
                <DFormCheck
                    id="invoice_changes"
                    v-model="upgradeForm.invoice_changes"
                />
            </DFormRow>

            <!-- Start Date -->
            <DFormRow
                :label="__('customers.contracts.start_date')"
                :error="errors.start_date"
                is-required
            >
                <DDateFormatInput
                    id="start_date"
                    v-model="upgradeForm.start_date"
                    format="YYYY-MM-DD"
                    add-label-classes="required"
                    type="date"
                    @valueChanged="resetForce"
                />
            </DFormRow>
        </div>
        <template #actions>
            <DAlert
                v-if="errors.warning"
                class="m-0"
                color="warning"
            >
                {{ errors.warning }}
            </DAlert>
            <DButton
                v-if="upgradeForm.force === false"
                :label="__('messages.confirm')"
                :disabled="isInLoadingState"
                color="primary"
                @click="upgradeContractConfirm"
            />
            <DButton
                v-else
                :label="__('messages.apply')"
                :disabled="isInLoadingState"
                class="ms-1"
                color="success"
                @click="upgradeContractConfirm"
            />
        </template>
    </DModal>
</template>

<script>
import Price from '~@/Components/Customers/Contracts/FormFields/Price.vue'
import Discount from '~@/Components/Customers/Contracts/FormFields/Discount.vue'
import ThirdPartyCost from '~@/Components/Customers/Contracts/FormFields/ThirdPartyCost.vue'
import PhaseText from '~@/Components/Customers/Contracts/FormFields/PhaseText'
import ProductChangesSelection from '~@/Components/Products/Products/ProductChangesSelection'

export default {
    name: 'ProductUpgradeModal',
    components: {
        Price,
        ThirdPartyCost,
        Discount,
        PhaseText,
        ProductChangesSelection
    },
    props: {
        contract: {
            type: Object,
            default: () => { return {} }
        },
        errorsData: {
            type: Object,
            default: () => { return {} }
        },
        showModal: {
            type: Boolean,
            default: false
        },
        activeProductId: {
            type: Number,
            default: 0
        },
        discountTypes: {
            type: Array,
            default: () => { return [] }
        },
        marketRegionId: {
            type: Number,
            default: null
        }
    },
    emits: ['close-modal'],
    data () {
        return {
            upgradeForm: this.$inertia.form({
                product_id: '',
                plan_id: '',
                invoice_changes: false,
                start_date: new Date().toISOString().split('T')[0],
                force: false,
                activity_log_comment: '',
                incident_id: null,
                price: null,
                discount_name: null,
                discount_type: null,
                discount_value: null,
                third_party_cost: null,
                phase_text: null
            }),
            errors: this.errorsData,
            pricePlans: []
        }
    },
    methods: {
        upgradeContractConfirm () {
            this.upgradeForm.post(this.$route('customers.customers.contracts.upgrade', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }), {
                onSuccess: (response) => {
                    const changed = response.props.flash.errorMessage === null
                    this.$emit('close-modal', changed)
                    this.resetUpgradeForm()
                },
                onError: (error) => {
                    this.errors = error
                    if (this.errors.warning) {
                        this.upgradeForm.force = true
                    }
                }
            })
        },
        onProductSelection (value) {
            if (value.value === this.activeProductId) {
                this.upgradeForm.price = this.contract.active_contract_phase.price
                this.upgradeForm.discount_name = this.contract.active_contract_phase.discount_name
                this.upgradeForm.discount_type = this.contract.active_contract_phase.discount_type
                this.upgradeForm.discount_value = this.contract.active_contract_phase.discount_value
                this.upgradeForm.third_party_cost = this.contract.active_contract_phase.third_party_cost
                this.upgradeForm.phase_text = this.contract.active_contract_phase.phase_text
            } else {
                this.upgradeForm.price = null
                this.upgradeForm.discount_name = null
                this.upgradeForm.discount_type = null
                this.upgradeForm.discount_value = null
                this.upgradeForm.third_party_cost = null
                this.upgradeForm.phase_text = null
            }

            this.loadProductPlans(value)
        },
        loadProductPlans (value) {
            this.upgradeForm.product_id = value.value
            this.resetForce()

            const fetchData = async () => {
                const { data: pricePlans } = await window.axios.get(this.$route('products.dropdown.plans', {
                    product: this.upgradeForm.product_id,
                    market_region_id: this.marketRegionId
                }))

                this.pricePlans = pricePlans
            }

            fetchData()
        },
        resetUpgradeForm () {
            this.upgradeForm = this.$inertia.form({
                product_id: '',
                plan_id: '',
                invoice_changes: false,
                force: false,
                start_date: new Date().toISOString().split('T')[0],
                incident_id: null,
                price: null,
                discount_name: null,
                discount_type: null,
                discount_value: null,
                third_party_cost: null,
                phase_text: null
            })
            this.errors = {}
        },
        resetForce () {
            this.upgradeForm.force = false
        },
        setIncident (value) {
            this.upgradeForm.incident_id = value
        }
    }
}
</script>
